// =============================================================================
// Admin Analytics — Reports view
// =============================================================================
// Pillars 1–4 of the metric spec: report activity, user productivity, QA, and
// cycle time. Each widget is one cell of the `adminAnalytics` GraphQL resource:
// on load every cell reads from the single dashboard request; a widget's own
// time override / category / page switches just that widget to its own request
// (see `data/hooks.ts`). Every widget exposes the rows its number is computed
// from behind "Raw Data" — a server-paginated drilldown with server facets.
// =============================================================================
import { ArrowDownRightIcon, ArrowUpRightIcon, ArrowsRightLeftIcon } from "@heroicons/react/24/outline";
import { clsx } from "clsx";
import { addDays, format, max, min, parseISO } from "date-fns";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { CalendarDateRange, Column, Label, Tooltip } from "@fronterahealth/frontera-ui-components";

import {
  AnalyticsOverdueReportSortField,
  AnalyticsOverdueRowFieldsFragment,
  AnalyticsQaByUserPageFieldsFragment,
  AnalyticsQaCategory,
  AnalyticsQaFlagRowFieldsFragment,
  AnalyticsQaFlagSortField,
  AnalyticsQaFlagStatus,
  AnalyticsReportDateField,
  AnalyticsReportRowFieldsFragment,
  AnalyticsReportSortField,
  AnalyticsSortDirection,
  ReportAnalyticsStatus,
  ReportAnalyticsTemplateType,
} from "@api/graphql/types-and-hooks";
import { useAdminAnalytics } from "@pages/AdminAnalytics/AdminAnalyticsProvider";
import { CellBody } from "@pages/AdminAnalytics/components/CellBody";
import { CellErrorState } from "@pages/AdminAnalytics/components/CellErrorState";
import { FacetField, FilterValues } from "@pages/AdminAnalytics/components/DrilldownFilters";
import { MetricCard } from "@pages/AdminAnalytics/components/MetricCard";
import { ExportField, ServerDrilldownDialog } from "@pages/AdminAnalytics/components/ServerDrilldownDialog";
import { WidgetIconButton, useWidgetTimeOverride } from "@pages/AdminAnalytics/components/WidgetChrome";
import { WidgetContainer } from "@pages/AdminAnalytics/components/WidgetContainer";
import {
  BarList,
  ChartSkeleton,
  ColumnChart,
  EmptyCell,
  StatusBarList,
  StatusStackedBar,
  cellText,
} from "@pages/AdminAnalytics/components/charts";
import { usePageState } from "@pages/AdminAnalytics/components/usePageState";
import { ServerDrilldown, useServerDrilldown } from "@pages/AdminAnalytics/components/useServerDrilldown";
import { CellFilters, buildDrilldownFilters, toCellFiltersInput } from "@pages/AdminAnalytics/data/analyticsFilters";
import {
  CATEGORY_FROM_API,
  CATEGORY_TO_API,
  STATUS_FROM_API,
  STATUS_TO_API,
  TEMPLATE_TYPE_FROM_API,
} from "@pages/AdminAnalytics/data/enumMaps";
import {
  IN_CELL_PAGE_SIZE,
  REPORTS_BY_USER_LIMIT,
  resolveTemplateTypes,
  useFacetScope,
  useOpenQaFlagsCell,
  useOverdueReportDrilldown,
  useOverdueReportsCell,
  usePageFilters,
  useQaByUserCell,
  useQaFlagDrilldown,
  useReportDrilldown,
  useReportsByStatusCell,
  useReportsByUserCell,
  useResolvedQaFlagsCell,
  useSubmissionTrendCell,
  useTotalReportsCell,
  useWritingTimeCell,
} from "@pages/AdminAnalytics/data/hooks";
import {
  QA_CATEGORIES,
  QA_CATEGORY_LABELS,
  QaCategory,
  REPORT_STATUS_LABELS,
  TEMPLATE_TYPE_LABELS,
  TemplateType,
} from "@pages/AdminAnalytics/data/types";
import {
  ADMIN_ANALYTICS_TITLES,
  ALL_REPORT_STATUSES,
  BASE_REPORT_STATUSES,
  STATUS_COLORS,
  STATUS_DEFINITIONS,
  calculatePercentageChange,
  formatMinutesToHrMin,
  formatPercent,
  formatTemplateName,
  providerDetailUrl,
} from "@pages/AdminAnalytics/helpers";
import { RawDataTableWrapper } from "@pages/VideoAnalytics/components/RawDataTableWrapper";

type ReportRow = AnalyticsReportRowFieldsFragment;
type QaFlagRow = AnalyticsQaFlagRowFieldsFragment;
type OverdueRow = AnalyticsOverdueRowFieldsFragment;
type QaByUserRow = AnalyticsQaByUserPageFieldsFragment["rows"][number];

// ----- Labels + links ---------------------------------------------------------

const templateTypeLabel = (t: ReportAnalyticsTemplateType | null | undefined): string | undefined =>
  t ? TEMPLATE_TYPE_LABELS[TEMPLATE_TYPE_FROM_API[t]] : undefined;

const statusLabel = (s: ReportAnalyticsStatus | null | undefined): string | undefined =>
  s ? REPORT_STATUS_LABELS[STATUS_FROM_API[s]] : undefined;

const categoryLabel = (c: AnalyticsQaCategory): string => QA_CATEGORY_LABELS[CATEGORY_FROM_API[c]];

const templateLabel = (g: string | null | undefined): string | undefined => (g ? formatTemplateName(g) : undefined);

/** QA flag ids are relay ids; show the numeric flag id, as the table always has. */
const flagNumber = (flagId: string): string => {
  try {
    return atob(flagId).split(":").pop() ?? flagId;
  } catch {
    return flagId;
  }
};

/** Builds the in-app URL for a given report row. Used for row-click navigation and deep links. */
const reportPageUrl = (r: { reportId: string; reportType?: ReportAnalyticsTemplateType | null }): string => {
  switch (r.reportType) {
    case ReportAnalyticsTemplateType.Ra:
      return `/clinical-reports/reassessment/${r.reportId}`;
    case ReportAnalyticsTemplateType.Db:
      return `/clinical-reports/evaluations/${r.reportId}`;
    case ReportAnalyticsTemplateType.Ia:
    default:
      return `/clinical-reports/assessment-reports/${r.reportId}`;
  }
};

const ProviderLink: React.FC<{ id?: string | null; name: string | null | undefined }> = ({ id, name }) => {
  if (!name) return <EmptyCell />;
  return id ? (
    <Link
      to={providerDetailUrl(id)}
      className="block truncate hover:underline"
      title={name}
      onClick={(e) => e.stopPropagation()}
    >
      {name}
    </Link>
  ) : (
    <span className="block truncate" title={name}>
      {name}
    </span>
  );
};

const desc = <F,>(field: F) => ({ field, direction: AnalyticsSortDirection.Desc });

const categorySeed = (category: QaCategory | "all"): FilterValues =>
  category === "all" ? {} : { category: [CATEGORY_TO_API[category]] };

// ----- Raw drill-down columns, CSV fields, and facets --------------------------

/**
 * One drill-down column, which drives both the table and the CSV so they can't
 * drift. `text` is the plain value — the CSV cell, and the table cell (via
 * `cellText`) unless `render` draws something richer from the same row.
 */
interface DrilldownField<T> {
  title: string;
  key: string;
  dataIndex: keyof T;
  text: (row: T) => string | null | undefined;
  render?: (row: T) => React.ReactNode;
}

const toColumns = <T,>(fields: DrilldownField<T>[]): Column<T>[] =>
  fields.map(({ title, key, dataIndex, text, render }) => ({
    title,
    key,
    dataIndex,
    render: (_value, row) => (render ? render(row) : cellText(text(row))),
  }));

const toExportFields = <T,>(fields: DrilldownField<T>[]): ExportField<T>[] =>
  fields.map(({ title, text }) => ({ label: title, get: (row) => text(row) ?? "" }));

/** Dates the table shows as an em dash when missing (the CSV leaves them blank). */
const dashIfMissing = (v: string | null | undefined): React.ReactNode => v || "—";

const learnerField: DrilldownField<ReportRow> = {
  title: "Learner",
  key: "learner",
  dataIndex: "learnerName",
  text: (r) => r.learnerName,
};
const reportTypeField: DrilldownField<ReportRow> = {
  title: "Report Type",
  key: "reportType",
  dataIndex: "reportType",
  text: (r) => templateTypeLabel(r.reportType),
};
const templateField: DrilldownField<ReportRow> = {
  title: "Template",
  key: "templateGroup",
  dataIndex: "templateGroup",
  text: (r) => templateLabel(r.templateGroup),
};
const providerField: DrilldownField<ReportRow> = {
  title: "Provider",
  key: "provider",
  dataIndex: "providerName",
  text: (r) => r.providerName,
  render: (r) => <ProviderLink id={r.providerId} name={r.providerName} />,
};
const statusField: DrilldownField<ReportRow> = {
  title: "Status",
  key: "status",
  dataIndex: "status",
  text: (r) => statusLabel(r.status),
};

const REPORT_FIELDS: DrilldownField<ReportRow>[] = [
  learnerField,
  reportTypeField,
  templateField,
  providerField,
  statusField,
  { title: "Created", key: "createdDate", dataIndex: "createdDate", text: (r) => r.createdDate },
];

const WRITING_FIELDS: DrilldownField<ReportRow>[] = [
  learnerField,
  reportTypeField,
  templateField,
  providerField,
  {
    title: "Completed",
    key: "completedDate",
    dataIndex: "completedDate",
    text: (r) => r.completedDate,
    render: (r) => dashIfMissing(r.completedDate),
  },
  {
    title: "Writing time",
    key: "writingMinutes",
    dataIndex: "writingMinutes",
    text: (r) => (typeof r.writingMinutes === "number" ? formatMinutesToHrMin(r.writingMinutes) : undefined),
    render: (r) => (typeof r.writingMinutes === "number" ? formatMinutesToHrMin(r.writingMinutes) : <EmptyCell />),
  },
];

const SUBMISSION_FIELDS: DrilldownField<ReportRow>[] = [
  learnerField,
  reportTypeField,
  templateField,
  providerField,
  statusField,
  {
    title: "Submitted",
    key: "submittedDate",
    dataIndex: "submittedDate",
    text: (r) => r.submittedDate,
    render: (r) => dashIfMissing(r.submittedDate),
  },
];

const QA_FIELDS: DrilldownField<QaFlagRow>[] = [
  { title: "Flag", key: "flagId", dataIndex: "flagId", text: (r) => flagNumber(r.flagId) },
  { title: "Category", key: "category", dataIndex: "category", text: (r) => categoryLabel(r.category) },
  { title: "Report Type", key: "reportType", dataIndex: "reportType", text: (r) => templateTypeLabel(r.reportType) },
  {
    title: "Provider",
    key: "provider",
    dataIndex: "providerName",
    text: (r) => r.providerName,
    render: (r) => <ProviderLink id={r.providerId} name={r.providerName} />,
  },
  { title: "Raised", key: "createdDate", dataIndex: "createdDate", text: (r) => r.createdDate },
  {
    title: "Status",
    key: "resolved",
    dataIndex: "resolved",
    text: (r) => (r.resolved ? "Resolved" : "Open"),
    render: (r) => (r.resolved ? "Resolved" : "Open"),
  },
  {
    title: "Resolved",
    key: "resolvedDate",
    dataIndex: "resolvedDate",
    text: (r) => r.resolvedDate,
    render: (r) => dashIfMissing(r.resolvedDate),
  },
];

const OVERDUE_FIELDS: DrilldownField<OverdueRow>[] = [
  { title: "Report Type", key: "reportType", dataIndex: "reportType", text: (r) => templateTypeLabel(r.reportType) },
  { title: "Template", key: "templateGroup", dataIndex: "templateGroup", text: (r) => templateLabel(r.templateGroup) },
  { title: "Learner", key: "learner", dataIndex: "learnerName", text: (r) => r.learnerName },
  { title: "Provider", key: "provider", dataIndex: "providerName", text: (r) => r.providerName },
  { title: "Created", key: "createdDate", dataIndex: "createdDate", text: (r) => r.createdDate },
  {
    title: "Days open",
    key: "daysOpen",
    dataIndex: "daysOpen",
    text: (r) => String(r.daysOpen),
    render: (r) => <span className="font-medium text-red-600">{String(r.daysOpen)}</span>,
  },
];

const RAW_QA_COLUMNS = toColumns(QA_FIELDS);
const QA_EXPORT = toExportFields(QA_FIELDS);
const OVERDUE_COLUMNS = toColumns(OVERDUE_FIELDS);
const OVERDUE_EXPORT = toExportFields(OVERDUE_FIELDS);

// Facet keys are the backend's; labels are ours. Date facets are replaced by
// the dialog's date-range chip.
const LEARNER_FACET: FacetField = { key: "learner", label: "Learner" };
const REPORT_TYPE_FACET: FacetField = { key: "reportType", label: "Report Type" };
const TEMPLATE_FACET: FacetField = { key: "templateGroup", label: "Template" };
const PROVIDER_FACET: FacetField = { key: "provider", label: "Provider" };
const STATUS_FACET: FacetField = { key: "status", label: "Status" };
const CATEGORY_FACET: FacetField = { key: "category", label: "Category" };
const QA_STATUS_FACET: FacetField = { key: "qaStatus", label: "Status" };

const REPORT_FACETS = [LEARNER_FACET, REPORT_TYPE_FACET, TEMPLATE_FACET, PROVIDER_FACET, STATUS_FACET];
const WRITING_FACETS = [LEARNER_FACET, REPORT_TYPE_FACET, TEMPLATE_FACET, PROVIDER_FACET];
const QA_FACETS = [CATEGORY_FACET, REPORT_TYPE_FACET, PROVIDER_FACET];
const OVERDUE_FACETS = [REPORT_TYPE_FACET, TEMPLATE_FACET, LEARNER_FACET, PROVIDER_FACET];

// ----- Server drilldowns -----------------------------------------------------

type ReportDrilldownKind = "created" | "completed" | "submitted";

const REPORT_DRILLDOWNS: Record<
  ReportDrilldownKind,
  {
    dateField: AnalyticsReportDateField;
    sortField: AnalyticsReportSortField;
    columns: Column<ReportRow>[];
    exportFields: ExportField<ReportRow>[];
    facetFields: FacetField[];
  }
> = {
  created: {
    dateField: AnalyticsReportDateField.Created,
    sortField: AnalyticsReportSortField.CreatedDate,
    columns: toColumns(REPORT_FIELDS),
    exportFields: toExportFields(REPORT_FIELDS),
    facetFields: REPORT_FACETS,
  },
  completed: {
    dateField: AnalyticsReportDateField.Completed,
    sortField: AnalyticsReportSortField.CompletedDate,
    columns: toColumns(WRITING_FIELDS),
    exportFields: toExportFields(WRITING_FIELDS),
    facetFields: WRITING_FACETS,
  },
  submitted: {
    dateField: AnalyticsReportDateField.Submitted,
    sortField: AnalyticsReportSortField.SubmittedDate,
    columns: toColumns(SUBMISSION_FIELDS),
    exportFields: toExportFields(SUBMISSION_FIELDS),
    facetFields: REPORT_FACETS,
  },
};

interface DrilldownProps {
  title: string;
  exportFileName: string;
  drilldown: ServerDrilldown;
  /** The widget's own filters (time override etc.) that the dialog starts from. */
  base: CellFilters;
}

const ReportsDrilldown: React.FC<DrilldownProps & { kind: ReportDrilldownKind }> = ({
  title,
  exportFileName,
  drilldown,
  base,
  kind,
}) => {
  const navigate = useNavigate();
  const config = REPORT_DRILLDOWNS[kind];
  const facetScope = useFacetScope(base);
  const result = useReportDrilldown({
    open: drilldown.open,
    session: drilldown.session,
    cell: buildDrilldownFilters(base, drilldown.dateRange, drilldown.query.selections, facetScope),
    page: drilldown.query.page,
    facetSearch: drilldown.query.facetSearch,
    sort: desc(config.sortField),
    dateField: config.dateField,
  });
  return (
    <ServerDrilldownDialog
      title={title}
      drilldown={drilldown}
      result={result}
      columns={config.columns}
      facetFields={config.facetFields}
      facetScope={facetScope}
      exportFields={config.exportFields}
      exportFileName={exportFileName}
      onRowClick={(r) => {
        drilldown.setOpen(false);
        void navigate(reportPageUrl(r));
      }}
    />
  );
};

const QaFlagsDrilldown: React.FC<
  DrilldownProps & { facetFields: FacetField[]; sortField: AnalyticsQaFlagSortField }
> = ({ title, exportFileName, drilldown, base, facetFields, sortField }) => {
  const facetScope = useFacetScope(base);
  const result = useQaFlagDrilldown({
    open: drilldown.open,
    session: drilldown.session,
    cell: buildDrilldownFilters(base, drilldown.dateRange, drilldown.query.selections, facetScope),
    page: drilldown.query.page,
    facetSearch: drilldown.query.facetSearch,
    sort: desc(sortField),
  });
  return (
    <ServerDrilldownDialog
      title={title}
      drilldown={drilldown}
      result={result}
      columns={RAW_QA_COLUMNS}
      facetFields={facetFields}
      facetScope={facetScope}
      exportFields={QA_EXPORT}
      exportFileName={exportFileName}
    />
  );
};

const OverdueDrilldown: React.FC<DrilldownProps> = ({ title, exportFileName, drilldown, base }) => {
  const navigate = useNavigate();
  const facetScope = useFacetScope(base);
  const result = useOverdueReportDrilldown({
    open: drilldown.open,
    session: drilldown.session,
    cell: buildDrilldownFilters(base, drilldown.dateRange, drilldown.query.selections, facetScope),
    page: drilldown.query.page,
    facetSearch: drilldown.query.facetSearch,
    sort: desc(AnalyticsOverdueReportSortField.DaysOpen),
  });
  return (
    <ServerDrilldownDialog
      title={title}
      drilldown={drilldown}
      result={result}
      columns={OVERDUE_COLUMNS}
      facetFields={OVERDUE_FACETS}
      facetScope={facetScope}
      exportFields={OVERDUE_EXPORT}
      exportFileName={exportFileName}
      onRowClick={(r) => {
        drilldown.setOpen(false);
        void navigate(reportPageUrl(r));
      }}
    />
  );
};

/** Page resets whenever the page-level or the cell's own filters change. */
const usePageResetKey = (cell: CellFilters): string => {
  const pageFilters = usePageFilters();
  return JSON.stringify([pageFilters, toCellFiltersInput(cell) ?? null]);
};

// ----- Pillar 1: Total reports -----------------------------------------------

const TotalReportsWidget: React.FC = () => {
  const timeOverride = useWidgetTimeOverride();
  const drilldown = useServerDrilldown();
  const base: CellFilters = { dateRange: timeOverride.override };
  const { data, isFetching, isError, refetch } = useTotalReportsCell(base);
  const previousValue = data?.previousValue ?? undefined;
  const change = data && previousValue !== undefined ? calculatePercentageChange(data.value, previousValue) : undefined;
  return (
    <>
      <MetricCard
        title={ADMIN_ANALYTICS_TITLES.TOTAL_REPORTS}
        infoTooltip="All reports created within the selected date range."
        value={data?.value ?? 0}
        unit="reports"
        previousValue={previousValue}
        percentageChange={change?.change}
        isPositiveChange={change?.isPositive}
        loading={isFetching}
        error={isError ? <CellErrorState onRetry={refetch} /> : undefined}
        timeOverride={timeOverride}
        onViewData={() => drilldown.openWith()}
      />
      <ReportsDrilldown
        title="Total Reports — Raw Data"
        exportFileName="total-reports"
        drilldown={drilldown}
        base={base}
        kind="created"
      />
    </>
  );
};

// ----- Pillar 2 (Beta): Writing time by report type --------------------------
// Writing time is a MEDIAN computed separately per report type, and a type only
// appears once it has >= 20 real writing-time data points in the queried range.
// So instead of one hero number, this renders a short list (one row per type).
//
// The widget's own "show these report types" filter can diverge from the
// global Report Type filter: it's sent as the cell's `reportTypes`, which
// replaces the page-level value for this cell only.

const notEnoughWritingTimeDataTooltip = (typeLabel: string): string =>
  `Fewer than 20 ${typeLabel} reports have been completed in this range, so there isn't enough data yet for a reliable number.`;

const WRITING_TIME_INFO_TOOLTIP =
  "The typical time it takes to write a report, from first opening it to marking it complete, shown separately for each report type since they vary a lot in length. A report type only shows a number once at least 20 reports of that type have been completed in the selected range — below that, the number would be too easy to skew.";

/** Widget-local override of which report types are shown, independent of the global Report Type filter. */
const ReportTypeOverrideFilter: React.FC<{
  enabledTemplates: TemplateType[];
  value: TemplateType[];
  onChange: (v: TemplateType[]) => void;
}> = ({ enabledTemplates, value, onChange }) => (
  <div className="flex flex-col gap-2">
    <Label size="extra-small" displayType="loud" colorType="primary">
      Show report types
    </Label>
    <div className="flex flex-col gap-1.5">
      {enabledTemplates.map((type) => {
        const checked = value.includes(type);
        return (
          <label key={type} className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={checked}
              onChange={() => {
                // Never allow unchecking down to zero — that's ambiguous with
                // "no filter" (which means "all") rather than "show nothing".
                const next = checked ? value.filter((t) => t !== type) : [...value, type];
                if (next.length > 0) onChange(next);
              }}
              className="h-3.5 w-3.5 rounded-sm border-limestone-300 text-text-primary focus:ring-0"
            />
            <Label size="extra-small" colorType="primary" className="text-gray-700">
              {TEMPLATE_TYPE_LABELS[type]}
            </Label>
          </label>
        );
      })}
    </div>
  </div>
);

const WritingTimeByReportTypeWidget: React.FC = () => {
  const { templateType, orgConfig } = useAdminAnalytics();
  const timeOverride = useWidgetTimeOverride();
  const drilldown = useServerDrilldown();
  const [reportTypeOverride, setReportTypeOverride] = useState<TemplateType[] | undefined>(undefined);

  const effectiveGlobalTypes = resolveTemplateTypes(templateType, orgConfig.enabledTemplates, false);
  // What's actually shown: the widget's override if set, else whatever the
  // global filter currently resolves to (falling back to every enabled type
  // when the global filter is "all").
  const visibleTypes =
    reportTypeOverride ?? (effectiveGlobalTypes.length > 0 ? effectiveGlobalTypes : orgConfig.enabledTemplates);

  const base: CellFilters = { dateRange: timeOverride.override, reportTypes: reportTypeOverride };
  const { data, isFetching, isError, refetch } = useWritingTimeCell(base);
  const byType = new Map((data ?? []).map((r) => [TEMPLATE_TYPE_FROM_API[r.reportType], r]));

  return (
    <>
      <WidgetContainer
        title={ADMIN_ANALYTICS_TITLES.AVG_WRITING_TIME}
        infoTooltip={WRITING_TIME_INFO_TOOLTIP}
        subtitle="Median active writing time by report type"
        timeOverride={timeOverride}
        onViewData={() => drilldown.openWith()}
        badges={["beta"]}
        filterSlot={
          <ReportTypeOverrideFilter
            enabledTemplates={orgConfig.enabledTemplates}
            value={visibleTypes}
            onChange={setReportTypeOverride}
          />
        }
        extraActive={reportTypeOverride !== undefined}
        onResetExtra={() => setReportTypeOverride(undefined)}
      >
        <CellBody
          loading={isFetching}
          skeleton={<ChartSkeleton rows={visibleTypes.length} />}
          error={isError}
          onRetry={refetch}
        >
          <div className="flex flex-col gap-3">
            {visibleTypes.map((type) => {
              const row = byType.get(type);
              const previous = row?.previousMedianWritingMinutes ?? undefined;
              const change =
                row && previous !== undefined
                  ? calculatePercentageChange(row.medianWritingMinutes, previous)
                  : undefined;
              return (
                <div key={type} className="flex items-center justify-between gap-3">
                  <Label size="extra-small" colorType="primary" className="text-gray-700">
                    {TEMPLATE_TYPE_LABELS[type]}
                  </Label>
                  {row ? (
                    <div className="flex items-baseline gap-2">
                      <Label size="small" colorType="primary">
                        {formatMinutesToHrMin(row.medianWritingMinutes)}
                      </Label>
                      {change && change.change !== 0 && (
                        <span
                          className={clsx(
                            "flex items-center text-xs",
                            // Lower writing time is "better", so invert the usual color direction.
                            change.isPositive ? "text-red-500" : "text-green-500",
                          )}
                        >
                          {change.isPositive ? (
                            <ArrowUpRightIcon className="mr-0.5 h-3 w-3" />
                          ) : (
                            <ArrowDownRightIcon className="mr-0.5 h-3 w-3" />
                          )}
                          {Math.abs(change.change)}%
                        </span>
                      )}
                    </div>
                  ) : (
                    <Tooltip text={notEnoughWritingTimeDataTooltip(TEMPLATE_TYPE_LABELS[type])} showInfoIcon={false}>
                      <Label size="extra-small" colorType="secondary" className="cursor-default italic">
                        Not enough data
                      </Label>
                    </Tooltip>
                  )}
                </div>
              );
            })}
          </div>
        </CellBody>
      </WidgetContainer>
      <ReportsDrilldown
        title="Avg. Report Writing Time — Raw Data"
        exportFileName="avg-writing-time"
        drilldown={drilldown}
        base={base}
        kind="completed"
      />
    </>
  );
};

// ----- QA category filter (renders inside the widget filter popover) ----------

const QaCategoryFilter: React.FC<{
  value: QaCategory | "all";
  onChange: (v: QaCategory | "all") => void;
}> = ({ value, onChange }) => {
  const options: Array<{ label: string; value: QaCategory | "all" }> = [
    { label: "All", value: "all" },
    ...QA_CATEGORIES.map((c) => ({ label: QA_CATEGORY_LABELS[c], value: c })),
  ];
  return (
    <div className="flex flex-col gap-2">
      <Label size="extra-small" displayType="loud" colorType="primary">
        Category
      </Label>
      <div className="flex flex-wrap gap-1">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={clsx(
              "rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors",
              value === opt.value
                ? "bg-text-primary text-white"
                : "bg-limestone-100 text-text-secondary hover:bg-limestone-200",
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// ----- Pillar 3: Open / Resolved QA flag counts ------------------------------

const OpenQaFlagsWidget: React.FC = () => {
  const timeOverride = useWidgetTimeOverride();
  const drilldown = useServerDrilldown();
  const [qaCategory, setQaCategory] = useState<QaCategory | "all">("all");
  const { data, isFetching, isError, refetch } = useOpenQaFlagsCell({
    dateRange: timeOverride.override,
    categories: qaCategory === "all" ? undefined : [qaCategory],
  });
  return (
    <>
      <MetricCard
        title={ADMIN_ANALYTICS_TITLES.OPEN_QA_FLAGS}
        infoTooltip="QA flags raised but not yet resolved across the org."
        value={data?.value ?? 0}
        unit="flags"
        loading={isFetching}
        error={isError ? <CellErrorState onRetry={refetch} /> : undefined}
        timeOverride={timeOverride}
        onViewData={() => drilldown.openWith({ selections: categorySeed(qaCategory) })}
        filterSlot={<QaCategoryFilter value={qaCategory} onChange={setQaCategory} />}
        extraActive={qaCategory !== "all"}
        onResetExtra={() => setQaCategory("all")}
      />
      <QaFlagsDrilldown
        title="Open QA Flags — Raw Data"
        exportFileName="open-qa-flags"
        drilldown={drilldown}
        base={{ dateRange: timeOverride.override, qaStatus: AnalyticsQaFlagStatus.Open }}
        facetFields={QA_FACETS}
        sortField={AnalyticsQaFlagSortField.CreatedDate}
      />
    </>
  );
};

const ResolvedQaFlagsWidget: React.FC = () => {
  const timeOverride = useWidgetTimeOverride();
  const drilldown = useServerDrilldown();
  const [qaCategory, setQaCategory] = useState<QaCategory | "all">("all");
  const { data, isFetching, isError, refetch } = useResolvedQaFlagsCell({
    dateRange: timeOverride.override,
    categories: qaCategory === "all" ? undefined : [qaCategory],
  });
  const value = data?.value ?? 0;
  const previousValue = data?.previousValue ?? undefined;
  const change = previousValue !== undefined ? calculatePercentageChange(value, previousValue) : undefined;
  return (
    <>
      <MetricCard
        title={ADMIN_ANALYTICS_TITLES.RESOLVED_QA_FLAGS}
        infoTooltip="QA flags marked resolved within the selected date range."
        value={value}
        unit="flags"
        previousValue={previousValue}
        percentageChange={change?.change}
        isPositiveChange={change?.isPositive}
        loading={isFetching}
        error={isError ? <CellErrorState onRetry={refetch} /> : undefined}
        timeOverride={timeOverride}
        onViewData={() => drilldown.openWith({ selections: categorySeed(qaCategory) })}
        filterSlot={<QaCategoryFilter value={qaCategory} onChange={setQaCategory} />}
        extraActive={qaCategory !== "all"}
        onResetExtra={() => setQaCategory("all")}
      />
      <QaFlagsDrilldown
        title="Resolved QA Flags — Raw Data"
        exportFileName="resolved-qa-flags"
        drilldown={drilldown}
        base={{ dateRange: timeOverride.override, qaStatus: AnalyticsQaFlagStatus.Resolved }}
        facetFields={QA_FACETS}
        sortField={AnalyticsQaFlagSortField.ResolvedDate}
      />
    </>
  );
};

// ----- Pillar 1: Reports by status -------------------------------------------

type StatusViewMode = "stacked" | "bars";

const ReportsByStatusWidget: React.FC = () => {
  const { orgConfig } = useAdminAnalytics();
  const timeOverride = useWidgetTimeOverride();
  const drilldown = useServerDrilldown();
  const [viewMode, setViewMode] = useState<StatusViewMode>("stacked");
  const base: CellFilters = { dateRange: timeOverride.override };
  const { data, isFetching, isError, refetch } = useReportsByStatusCell(base);

  const visibleStatuses = orgConfig.hasSubmissionIntegration ? ALL_REPORT_STATUSES : BASE_REPORT_STATUSES;
  const countMap = new Map((data ?? []).map((r) => [STATUS_FROM_API[r.status], r.count]));
  const total = visibleStatuses.reduce((sum, s) => sum + (countMap.get(s) ?? 0), 0);

  // Shared items shape used by both chart variants — per-status color + definition tooltip
  const statusItems = visibleStatuses.map((s) => ({
    label: REPORT_STATUS_LABELS[s],
    value: countMap.get(s) ?? 0,
    color: STATUS_COLORS[s],
    definition: STATUS_DEFINITIONS[s],
    onItemClick: () => drilldown.openWith({ selections: { status: [STATUS_TO_API[s]] } }),
  }));

  const toggleButton = (
    <WidgetIconButton
      Icon={ArrowsRightLeftIcon}
      tooltip="Switch view"
      onClick={() => setViewMode((v) => (v === "stacked" ? "bars" : "stacked"))}
    />
  );

  let statusChartContent: React.ReactNode;
  if (isFetching) {
    statusChartContent = <ChartSkeleton rows={visibleStatuses.length} />;
  } else if (isError) {
    statusChartContent = <CellErrorState onRetry={refetch} />;
  } else if (viewMode === "stacked") {
    statusChartContent = <StatusStackedBar items={statusItems} />;
  } else {
    statusChartContent = <StatusBarList items={statusItems} total={total} />;
  }

  return (
    <>
      {/* Always use WidgetContainer so the card shell never remounts on toggle */}
      <WidgetContainer
        title={ADMIN_ANALYTICS_TITLES.REPORTS_BY_STATUS}
        infoTooltip={
          orgConfig.hasSubmissionIntegration
            ? "Reports in the selected range broken down by current pipeline status, including submission and payor review stages."
            : "Reports in the selected range broken down by status — draft (in progress) or complete."
        }
        timeOverride={timeOverride}
        onViewData={() => drilldown.openWith()}
        badges={orgConfig.isCatalightOrg ? ["catalight"] : undefined}
        extraButtons={toggleButton}
      >
        {statusChartContent}
      </WidgetContainer>
      <ReportsDrilldown
        title="Reports by Status — Raw Data"
        exportFileName="reports-by-status"
        drilldown={drilldown}
        base={base}
        kind="created"
      />
    </>
  );
};

// ----- Pillar 2: Reports by user ---------------------------------------------

const ReportsByUserWidget: React.FC = () => {
  const timeOverride = useWidgetTimeOverride();
  const drilldown = useServerDrilldown();
  const base: CellFilters = { dateRange: timeOverride.override };
  const { data, isFetching, isError, refetch } = useReportsByUserCell(base);

  // Each bar carries its own provider, so two providers with the same name stay distinct.
  const items = (data?.rows ?? []).map((r) => ({
    id: r.providerId,
    label: r.providerName ?? "",
    value: r.count,
    secondary: r.previousCount ?? undefined,
    onClick: () =>
      drilldown.openWith({
        selections: { provider: [r.providerId] },
        labels: { provider: { [r.providerId]: r.providerName ?? "" } },
      }),
  }));

  return (
    <WidgetContainer
      title={ADMIN_ANALYTICS_TITLES.REPORTS_BY_USER}
      infoTooltip="Reports created per Provider in the selected range."
      subtitle="Relative output across the team — click a bar to see that Provider's reports"
      timeOverride={timeOverride}
      onViewData={() => drilldown.openWith()}
    >
      <CellBody loading={isFetching} skeleton={<ChartSkeleton />} error={isError} onRetry={refetch}>
        <BarList items={items} maxRows={REPORTS_BY_USER_LIMIT} totalCount={data?.totalCount} valueSuffix="" />
      </CellBody>
      <ReportsDrilldown
        title="Reports by User — Raw Data"
        exportFileName="reports-by-user"
        drilldown={drilldown}
        base={base}
        kind="created"
      />
    </WidgetContainer>
  );
};

// ----- Pillar 4: Submission trend --------------------------------------------

const SubmissionTrendWidget: React.FC = () => {
  const { globalRange } = useAdminAnalytics();
  const timeOverride = useWidgetTimeOverride();
  const drilldown = useServerDrilldown();
  const base: CellFilters = { dateRange: timeOverride.override };
  const { data, isFetching, isError, refetch } = useSubmissionTrendCell(base);

  const points = [...(data ?? [])]
    .sort((a, b) => String(a.weekStart).localeCompare(String(b.weekStart)))
    .map((p) => ({ label: format(parseISO(p.weekStart), "MMM d"), value: p.submitted, id: p.weekStart as string }));

  // Clicking a week narrows the dialog to that week — clipped to the widget's
  // range, since the bar only counts submissions inside it.
  const openWeek = (weekStart?: string) => {
    if (!weekStart) return drilldown.openWith();
    const range: CalendarDateRange | undefined = timeOverride.override ?? globalRange;
    const start = parseISO(weekStart);
    const end = addDays(start, 6);
    drilldown.openWith({
      dateRange: {
        startDate: range?.startDate ? max([start, range.startDate]) : start,
        endDate: range?.endDate ? min([end, range.endDate]) : end,
      },
    });
  };

  return (
    <WidgetContainer
      title={ADMIN_ANALYTICS_TITLES.SUBMISSION_TREND}
      infoTooltip="Submitted reports grouped by week of true submission timestamp."
      subtitle="Click a bar to see that week's submitted reports"
      timeOverride={timeOverride}
      onViewData={() => drilldown.openWith()}
    >
      <CellBody loading={isFetching} skeleton={<ChartSkeleton rows={3} />} error={isError} onRetry={refetch}>
        <ColumnChart points={points} onPointClick={(p) => openWeek(p.id)} />
      </CellBody>
      <ReportsDrilldown
        title="Submission Trend — Raw Data"
        exportFileName="submission-trend"
        drilldown={drilldown}
        base={base}
        kind="submitted"
      />
    </WidgetContainer>
  );
};

// ----- Pillar 4: Overdue reports ---------------------------------------------

/** The card showcases the most-overdue reports; the full set lives behind Raw Data. */
const OVERDUE_SHOWCASE_LIMIT = 20;

const OverdueReportsWidget: React.FC = () => {
  const navigate = useNavigate();
  const timeOverride = useWidgetTimeOverride();
  const drilldown = useServerDrilldown();
  const base: CellFilters = { dateRange: timeOverride.override };
  const [page, setPage] = usePageState(usePageResetKey(base), IN_CELL_PAGE_SIZE);
  const { data, isFetching, isError, refetch } = useOverdueReportsCell(base, page);

  const totalItemCount = Math.min(data?.totalCount ?? 0, OVERDUE_SHOWCASE_LIMIT);
  // Don't let the last showcase page spill past the cap.
  const rows = (data?.rows ?? []).slice(0, Math.max(0, OVERDUE_SHOWCASE_LIMIT - (page.page - 1) * page.pageSize));

  return (
    <WidgetContainer
      title={ADMIN_ANALYTICS_TITLES.OVERDUE_REPORTS}
      infoTooltip="Reports that have been open longer than expected, longest open first. 'Days open' counts calendar days since creation."
      subtitle="Reports open longer than expected — click a row to open that report"
      timeOverride={timeOverride}
      onViewData={() => drilldown.openWith()}
    >
      <CellBody loading={isFetching && !data} skeleton={<ChartSkeleton />} error={isError} onRetry={refetch}>
        <RawDataTableWrapper
          data={rows}
          columns={OVERDUE_COLUMNS}
          loading={isFetching}
          totalItemCount={totalItemCount}
          defaultItemsPerPage={page.pageSize}
          currentPage={page.page}
          onPageChange={setPage}
          onRowClick={(r) => {
            void navigate(reportPageUrl(r));
          }}
        />
      </CellBody>
      <OverdueDrilldown
        title="Overdue Reports — Raw Data"
        exportFileName="overdue-reports"
        drilldown={drilldown}
        base={base}
      />
    </WidgetContainer>
  );
};

// ----- Pillar 3: QA flags by user --------------------------------------------

const QA_BY_USER_FACETS = [...QA_FACETS, QA_STATUS_FACET];

const colHeader = (label: string, tip: string) => (
  <span className="flex items-center gap-1">
    {label}
    <Tooltip infoIconClassName="w-3.5! h-3.5! shrink-0" text={tip} />
  </span>
);

const QA_BY_USER_COLUMNS: Column<QaByUserRow>[] = [
  { title: "Provider", dataIndex: "providerName", key: "providerName", render: (v) => cellText(v as string) },
  {
    title: colHeader(
      "Total flags",
      "All QA flags raised on reports authored by this Provider in the selected period — open and resolved combined.",
    ),
    dataIndex: "totalFlags",
    key: "totalFlags",
  },
  {
    title: colHeader(
      "Resolved",
      "Flags marked as resolved. Attributed to the Provider who wrote the flagged report, not the reviewer who raised or resolved it.",
    ),
    dataIndex: "resolvedFlags",
    key: "resolvedFlags",
  },
  {
    title: colHeader(
      "Open",
      "Flags not yet resolved. Attributed to the report's author — use this to spot Providers whose work frequently needs follow-up.",
    ),
    dataIndex: "openFlags",
    key: "openFlags",
  },
  {
    title: colHeader(
      "Resolution rate",
      "Resolved ÷ Total flags. Higher means fewer outstanding revisions across this Provider's reports.",
    ),
    dataIndex: "resolutionRate",
    key: "resolutionRate",
    render: (v) => formatPercent(typeof v === "number" ? v : 0),
  },
];

const QaByUserWidget: React.FC = () => {
  const timeOverride = useWidgetTimeOverride();
  const drilldown = useServerDrilldown();
  const [qaCategory, setQaCategory] = useState<QaCategory | "all">("all");
  const cell: CellFilters = {
    dateRange: timeOverride.override,
    categories: qaCategory === "all" ? undefined : [qaCategory],
  };
  const [page, setPage] = usePageState(usePageResetKey(cell), IN_CELL_PAGE_SIZE);
  const { data, isFetching, isError, refetch } = useQaByUserCell(cell, page);

  return (
    <WidgetContainer
      title={ADMIN_ANALYTICS_TITLES.QA_BY_USER}
      infoTooltip="Per-Provider QA flag totals, resolved counts, and resolution rate (resolved ÷ total)."
      subtitle="Raw numbers only — click a Provider to see their flags"
      timeOverride={timeOverride}
      onViewData={() => drilldown.openWith({ selections: categorySeed(qaCategory) })}
      filterSlot={<QaCategoryFilter value={qaCategory} onChange={setQaCategory} />}
      extraActive={qaCategory !== "all"}
      onResetExtra={() => setQaCategory("all")}
    >
      <CellBody loading={isFetching && !data} skeleton={<ChartSkeleton />} error={isError} onRetry={refetch}>
        <RawDataTableWrapper
          data={data?.rows ?? []}
          columns={QA_BY_USER_COLUMNS}
          loading={isFetching}
          totalItemCount={data?.totalCount ?? 0}
          defaultItemsPerPage={page.pageSize}
          currentPage={page.page}
          onPageChange={setPage}
          onRowClick={(r) =>
            drilldown.openWith({
              selections: { ...categorySeed(qaCategory), provider: [r.providerId] },
              labels: { provider: { [r.providerId]: r.providerName ?? "" } },
            })
          }
        />
      </CellBody>
      <QaFlagsDrilldown
        title="QA Flags by User — Raw Data"
        exportFileName="qa-flags-by-user"
        drilldown={drilldown}
        base={{ dateRange: timeOverride.override }}
        facetFields={QA_BY_USER_FACETS}
        sortField={AnalyticsQaFlagSortField.CreatedDate}
      />
    </WidgetContainer>
  );
};

// ----- View layout ------------------------------------------------------------

export const ReportsView: React.FC = () => {
  const { orgConfig } = useAdminAnalytics();
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-4">
        <TotalReportsWidget />
        <OpenQaFlagsWidget />
        <ResolvedQaFlagsWidget />
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <ReportsByStatusWidget />
        <ReportsByUserWidget />
        <WritingTimeByReportTypeWidget />
      </div>
      {orgConfig.isCatalightOrg && <SubmissionTrendWidget />}
      <OverdueReportsWidget />
      <QaByUserWidget />
    </div>
  );
};
